import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, MessageCircle, Inbox } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export function AdminChatInterface() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  // Get active conversations
  const { data: conversations = [] } = useQuery({
    queryKey: ['admin-conversations'],
    queryFn: async () => {
      console.log('Fetching admin conversations...');
      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          profiles:user_id (email, first_name, last_name)
        `)
        .eq('status', 'active')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching conversations:', error);
        throw error;
      }
      console.log('Fetched conversations:', data);
      return data;
    },
    refetchInterval: 5000,
  });

  // Get offline requests
  const { data: offlineRequests = [] } = useQuery({
    queryKey: ['offline-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chat_offline_requests')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    refetchInterval: 10000,
  });

  // Get messages for selected conversation
  const { data: messages = [] } = useQuery({
    queryKey: ['admin-chat-messages', selectedConversation],
    queryFn: async () => {
      if (!selectedConversation) return [];
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', selectedConversation)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedConversation,
    refetchInterval: 2000,
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!selectedConversation) throw new Error('No conversation selected');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: selectedConversation,
          sender_id: user.id,
          sender_type: 'admin',
          message: text,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['admin-chat-messages', selectedConversation] });
    },
  });

  const markRequestRespondedMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('chat_offline_requests')
        .update({ status: 'responded', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offline-requests'] });
      toast({
        title: "Request marked as responded",
      });
    },
  });

  return (
    <div className="container mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Conversations</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="active">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="active">
                  Active ({conversations.length})
                </TabsTrigger>
                <TabsTrigger value="offline">
                  Offline ({offlineRequests.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="active">
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {conversations.map((conv: any) => (
                      <Button
                        key={conv.id}
                        variant={selectedConversation === conv.id ? 'secondary' : 'ghost'}
                        className="w-full justify-start"
                        onClick={() => setSelectedConversation(conv.id)}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        {conv.profiles?.first_name && conv.profiles?.last_name
                          ? `${conv.profiles.first_name} ${conv.profiles.last_name}`
                          : conv.profiles?.email}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="offline">
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {offlineRequests.map((request: any) => (
                      <Card key={request.id}>
                        <CardContent className="pt-4">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">{request.name || 'Anonymous'}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{request.email}</p>
                            <p className="text-sm">{request.message}</p>
                            <Button
                              size="sm"
                              onClick={() => markRequestRespondedMutation.mutate(request.id)}
                            >
                              Mark as Responded
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {selectedConversation ? 'Chat Messages' : 'Select a conversation'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col h-[600px]">
            {selectedConversation ? (
              <>
                <ScrollArea className="flex-1 mb-4">
                  <div className="space-y-4">
                    {messages.map((msg: any) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.sender_type === 'admin'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <span className="text-xs opacity-70">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                <div className="flex gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && message && sendMessageMutation.mutate(message)}
                    placeholder="Type your message..."
                  />
                  <Button
                    onClick={() => message && sendMessageMutation.mutate(message)}
                    disabled={!message || sendMessageMutation.isPending}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center space-y-2">
                  <Inbox className="h-12 w-12 mx-auto" />
                  <p>Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
